import {
	useMemo,
	ReactNode,
	FunctionComponent,
	ComponentProps,
	ChangeEvent
} from 'react';

import {
	CommunicationCenterIcon,
	Instagram,
	Messenger,
	PhoneSound,
	ProfileUnfilled,
	WebsiteAvailable,
	WebsiteUnavailable
} from '@/resources/assets/images';

import { Input } from '@/components/atoms/Input';
import {
	BusinessQuery,
	FilterQueryType
} from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';
import { FilterComponents } from '../../molecules/FilterComponents';
import type { FilterProps } from '../../../@types';

export const FilterContactInfo = ({
	appliedFilters,
	onFilterChange
}: FilterProps) => {
	const filtersCount = useMemo(() => {
		const countGroups = [
			['includeEmailsYes'],
			['includeFBMessenger'],
			['includeIGMessenger'],
			['includePhoneNumber'],
			['includeWebsitesYes', 'includeWebsitesNo']
		];

		return countGroups.reduce((count, group) => {
			if (
				group.some(key => appliedFilters?.[key as keyof BusinessQuery])
			) {
				return count + 1;
			}
			return count;
		}, 0);
	}, [appliedFilters]);

	const handleClearContactFilters = () => {
		const queryNew = {
			...appliedFilters,
			includeEmailsYes: undefined,
			includeFBMessenger: undefined,
			includeIGMessenger: undefined,
			includePhoneNumber: undefined,
			includeWebsitesYes: undefined,
			includeWebsitesNo: undefined,
			skip: 0
		};

		sendAnalytics({
			type: 'track',
			eventName: 'User cleared contact info filters',
			properties: { appliedFilters: queryNew }
		});

		onFilterChange(queryNew);
	};

	const handleToggleFilter = ({ name, value }: ChangeFiltersProps) => {
		const newQuery: BusinessQuery = {
			...appliedFilters,
			queryType: FilterQueryType.FILTER,
			limit: 50,
			skip: 0,
			includeEmailsYes:
				name === 'includeEmails'
					? value === 'true'
					: (appliedFilters?.includeEmailsYes ?? undefined),
			includeEmailsNo:
				name === 'includeEmails'
					? value === 'false'
					: (appliedFilters?.includeEmailsNo ?? undefined),
			includeFBMessenger:
				name === 'includeFBMessenger'
					? value === 'true'
					: (appliedFilters?.includeFBMessenger ?? undefined),
			includeIGMessenger:
				name === 'includeIGMessenger'
					? value === 'true'
					: (appliedFilters?.includeIGMessenger ?? undefined),
			includePhoneNumber:
				name === 'includePhoneNumber'
					? value === 'true'
					: (appliedFilters?.includePhoneNumber ?? undefined),
			includeWebsitesYes:
				name === 'website'
					? value === 'true'
					: (appliedFilters?.includeWebsitesYes ?? undefined),
			includeWebsitesNo:
				name === 'website'
					? value === 'false'
					: (appliedFilters?.includeWebsitesNo ?? undefined)
		};

		sendAnalytics({
			type: 'track',
			eventName: 'User changed filters',
			properties: {
				filterChanged: name,
				appliedFilters: newQuery
			}
		});

		onFilterChange(newQuery);
	};

	return (
		<FilterComponents.Wrapper moduleName="contact-info">
			<FilterComponents.Header
				icon={ProfileUnfilled}
				title="Contact Info"
				showClearFiltersButton={filtersCount > 0}
				activeFiltersCount={filtersCount}
				onClearFilters={handleClearContactFilters}
			/>

			<FilterComponents.Content>
				<StatusControl title="Contact Information">
					<CheckboxOption
						name="includeEmails"
						label="Email"
						icon={CommunicationCenterIcon}
						checked={appliedFilters?.includeEmailsYes === true}
						onChange={handleToggleFilter}
					/>

					<CheckboxOption
						name="includeFBMessenger"
						label="Facebook Messenger"
						icon={Messenger}
						checked={appliedFilters?.includeFBMessenger === true}
						onChange={handleToggleFilter}
					/>

					<CheckboxOption
						name="includeIGMessenger"
						label="Instagram Messenger"
						icon={Instagram}
						checked={appliedFilters?.includeIGMessenger === true}
						onChange={handleToggleFilter}
					/>

					<CheckboxOption
						name="includePhoneNumber"
						label="Phone Number"
						icon={PhoneSound}
						checked={appliedFilters?.includePhoneNumber === true}
						onChange={handleToggleFilter}
					/>
				</StatusControl>

				<StatusControl title="Website">
					<RadioOption
						name="website"
						value="true"
						label="Website"
						icon={WebsiteAvailable}
						checked={appliedFilters?.includeWebsitesYes || false}
						onChange={handleToggleFilter}
					/>
					<RadioOption
						name="website"
						value="false"
						label="No Website"
						icon={WebsiteUnavailable}
						checked={appliedFilters?.includeWebsitesNo || false}
						onChange={handleToggleFilter}
					/>
				</StatusControl>
			</FilterComponents.Content>
		</FilterComponents.Wrapper>
	);
};

type StatusControlProps = {
	title: string;
	children: ReactNode;
};

const StatusControl = ({ title, children }: StatusControlProps) => (
	<section className="flex flex-col gap-3 border-b border-dm-strong-mid-grey pb-4">
		<p className="text-sm text-dm-white">{title}</p>
		{children}
	</section>
);

type ChangeFiltersProps = {
	name: string;
	value: 'true' | 'false';
};

type FilterOptionProps = ChangeFiltersProps & {
	label: string;
	icon: FunctionComponent<ComponentProps<'svg'>>;
	onChange: (filter: ChangeFiltersProps) => void;
	checked: boolean;
};

const CheckboxOption = ({
	name,
	label,
	icon: Icon,
	checked,
	onChange
}: Omit<FilterOptionProps, 'value'>) => {
	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		onChange({
			name,
			value: e.currentTarget.checked ? 'true' : 'false'
		});
	};

	return (
		<label className="flex cursor-pointer items-center gap-2 text-sm text-white">
			<Input
				type="checkbox"
				size="sm"
				name={name}
				onChange={handleChange}
				className="[&:has(input:checked)_path]:fill-dm-gold"
				checked={checked}
			/>

			<span className="flex items-center gap-1">
				<Icon width={12} height={12} />
				{label}
			</span>
		</label>
	);
};

const RadioOption = ({
	name,
	value,
	label,
	icon: Icon,
	checked,
	onChange
}: FilterOptionProps) => {
	const handleChange = () => {
		onChange({
			name,
			value
		});
	};

	return (
		<label className="flex cursor-pointer items-center gap-2 text-sm text-white">
			<Input
				type="radio"
				size="sm"
				name={name}
				value={value}
				onChange={handleChange}
				checked={checked}
			/>

			<span className="flex items-center gap-1">
				<Icon width={12} height={12} />
				{label}
			</span>
		</label>
	);
};
